<!----------------- BEGIN TS ---------------------->
<script lang="ts">
import { initPopover } from "@/plugins/bootstrapSetup";
import { VideoJsPlayer } from "video.js";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";

@Component({
  components: {},
})
export default class TheFrameByFrameAdvanceButtons extends Vue {
  // ---------- Props ----------

  /** The video player element to advance frame-by-frame on */
  @Prop() activePlayer!: VideoJsPlayer;

  /** The framerate of the video if it exists (otherwise it's 30 fps) */
  @Prop() framerate!: number;

  /** The framerate of the video if it exists (otherwise it's 30 fps) */
  @Prop() userHasPaused!: boolean;

  // ------- Local Vars --------

  /** Only shows the buttons when the video is paused. */
  showButtons = false;

  /** Defines how big the step in frame should be. */
  VIDEO_STEP = 1;

  // --------- Watchers --------

  @Watch("userHasPaused")
  updateButtonVisibility(): void {
    this.toggleFrameByFrame();
  }
  // ------- Lifecycle ---------
  beforeDestroy(): void {
    // clean up when destroyed
    window.removeEventListener("keyup", this.handleKeyUpEvent);
  }

  mounted(): void {
    if (this.$refs.fbfButtons) {
      initPopover(this.$refs.fbfButtons as Element, undefined, false);
    }
  }

  // --------- Methods ---------
  /** Sets up (or tears down) the frame-by-frame keybindings and button visibility. */
  toggleFrameByFrame(): void {
    if (this.userHasPaused) {
      window.addEventListener("keyup", this.handleKeyUpEvent);
      this.showButtons = true;
    } else {
      window.removeEventListener("keyup", this.handleKeyUpEvent);
      this.showButtons = false;
    }
  }

  /** Handles the keypress event for advancing frames */
  handleKeyUpEvent(e: KeyboardEvent): void {
    const element = e.target as HTMLElement;
    // Ignore any keypresses that are in input fields and stuff
    if (element.nodeName === "BODY") {
      if (e.key == "]") {
        this.advanceFrame(1);
      } else if (e.key == "[") {
        this.advanceFrame(-1);
      }
    }
  }

  /** Advance the video frames. */
  advanceFrame(direction: number): void {
    const currentTime = this.activePlayer.currentTime();
    const moveBy = (direction * this.VIDEO_STEP) / this.framerate;
    this.activePlayer.currentTime(currentTime + moveBy);
  }
}
</script>
<!----------------- END TS ------------------------>

<!----------------- BEGIN HTML -------------------->
<template lang="html">
  <transition name="fade">
    <div
      class="btn-group arrow-buttons"
      role="group"
      v-show="showButtons"
      key="left"
      ref="fbfButtons"
    >
      <button
        type="button"
        class="btn btn-primary frame-advance-button btn-light"
        data-bs-toggle="popover"
        data-bs-trigger="hover"
        data-bs-container="#app-container"
        data-bs-html="true"
        title="<span>Go back one frame</span> <kbd>[</kbd>"
        @click="advanceFrame(-1)"
      >
        <b-icon class="chevron" icon="chevron-left" font-scale="2"></b-icon>
      </button>
      <button
        type="button"
        class="btn btn-light frame-advance-button right"
        @click="advanceFrame(1)"
        data-bs-toggle="popover"
        data-bs-trigger="hover"
        data-bs-container="#app-container"
        data-bs-html="true"
        title="<span>Go forward one frame</span> <kbd>]</kbd>"
      >
        <b-icon class="chevron" icon="chevron-right" font-scale="2"></b-icon>
      </button>
    </div>
  </transition>
</template>
<!----------------- END HTML ---------------------->

<!----------------- BEGIN SCSS -------------------->
<style scoped lang="scss">
.arrow-buttons {
  max-height: 71px;
  display: flex;

  .frame-advance-button.right {
    border-radius: 0px 0px 10px 0px;
    box-shadow: -1px 7px 4px 0px rgb(0 0 0 / 25%);
  }

  .frame-advance-button {
    box-shadow: 1px 7px 4px 0px rgb(0 0 0 / 25%);
    cursor: pointer;
    border-radius: 0px 0px 0px 10px;
    font-size: 14px;

    .chevron {
      width: 20px;
      @media only screen and (max-width: 600px),
        screen and (max-height: 550px) {
        width: 15px;
      }

      @media only screen and (max-width: 500px),
        screen and (max-height: 400px) {
        width: 10px;
      }
    }
  }
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
<!----------------- END SCSS ---------------------->
